import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LayoutGrid, X } from "lucide-react";
import { Button } from "../../components/ui/button/index.js";
import { SimpleTooltip } from "../../components/ui/tooltip/index.js";
import { SheetDescription, SheetTitle } from "../../components/ui/sheet/index.js";
import { cn } from "../../lib/utils.js";
import { WorkspaceInternalSidebarHeader } from "../../components/layout/WorkspaceInternalSidebarHeader.js";
import {
  workspaceInternalNavItemClass,
  workspaceInternalNavLabelClass,
} from "../../components/layout/workspace-internal-sidebar.js";
import {
  contactSheetDescriptionClass,
  contactSheetHeaderClass,
  contactSheetHeaderIconClass,
  contactSheetTitleClass,
} from "../contacts/contact-sheet-presentation.js";
import { REPORTS_NAV_ITEMS } from "./reports-nav.js";

function drawerNavItemClass(selected: boolean): string {
  return cn(
    "group flex min-h-11 w-full items-center gap-3 rounded-lg border-s-2 px-3 py-2.5 text-start no-underline transition-colors duration-150 motion-reduce:transition-none",
    "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent/40",
    selected
      ? "border-accent bg-surface-selected font-semibold text-foreground"
      : "border-transparent font-medium text-foreground/80 hover:bg-surface-hover",
  );
}

export function ReportsSidebar({
  collapsed,
  onCollapsedChange,
  onNavigate,
  onClose,
  variant = "sidebar",
}: {
  collapsed: boolean;
  onCollapsedChange?: (collapsed: boolean) => void;
  onNavigate?: () => void;
  onClose?: () => void;
  variant?: "sidebar" | "drawer";
}): React.JSX.Element {
  const { t } = useTranslation();
  const isDrawer = variant === "drawer";
  const showLabels = isDrawer || !collapsed;

  return (
    <div
      className="flex h-full min-h-0 w-full flex-col bg-surface"
      data-testid="reports-sidebar"
      data-collapsed={collapsed && !isDrawer ? "true" : "false"}
    >
      {isDrawer ? (
        <header className={contactSheetHeaderClass}>
          <div className="flex min-w-0 items-start gap-3">
            <span className={contactSheetHeaderIconClass} aria-hidden="true">
              <LayoutGrid size={16} />
            </span>
            <div className="grid min-w-0 gap-1">
              <SheetTitle
                className={cn(
                  contactSheetTitleClass,
                  "text-[1.0625rem] leading-tight tracking-[-0.02em]",
                )}
              >
                {t("reports.nav.label")}
              </SheetTitle>
              <SheetDescription className={contactSheetDescriptionClass}>
                {t("reports.nav.drawerDescription")}
              </SheetDescription>
            </div>
          </div>
          {onClose ? (
            <Button
              type="button"
              variant="ghost"
              className="size-8 shrink-0 p-0 text-muted hover:text-foreground"
              onClick={onClose}
              aria-label={t("common.close")}
            >
              <X className="size-4" aria-hidden="true" />
            </Button>
          ) : null}
        </header>
      ) : (
        <WorkspaceInternalSidebarHeader
          title={t("reports.nav.label")}
          collapsed={collapsed}
          onCollapsedChange={onCollapsedChange}
          expandLabel={t("reports.nav.expandSidebar")}
          collapseLabel={t("reports.nav.collapseSidebar")}
          collapseTestId="reports-sidebar-collapse"
        />
      )}

      <nav
        className={cn("min-h-0 flex-1 overflow-y-auto px-1.5 py-2", isDrawer && "px-3 py-4")}
        aria-label={t("reports.nav.label")}
        data-testid="reports-nav"
      >
        <ul className={cn("m-0 grid list-none p-0", isDrawer ? "gap-1.5" : "gap-0.5")}>
          {REPORTS_NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const fullLabel = t(item.labelKey);
            const shortLabel = t(item.shortLabelKey);
            const link = (
              <NavLink
                to={item.to}
                end={item.end}
                onClick={onNavigate}
                className={({ isActive }) =>
                  isDrawer
                    ? drawerNavItemClass(isActive)
                    : workspaceInternalNavItemClass(isActive, collapsed)
                }
                aria-label={fullLabel}
                data-testid={item.testId}
              >
                {({ isActive }) => (
                  <>
                    {isDrawer ? (
                      <span
                        className={cn(
                          "inline-grid size-8 shrink-0 place-items-center rounded-lg",
                          isActive
                            ? "bg-accent-soft text-accent"
                            : "bg-surface-muted text-muted group-hover:text-foreground",
                        )}
                      >
                        <Icon size={16} aria-hidden="true" />
                      </span>
                    ) : (
                      <Icon
                        size={16}
                        className={cn("shrink-0", isActive ? "text-accent" : "text-muted")}
                        aria-hidden="true"
                      />
                    )}
                    <span
                      className={
                        isDrawer
                          ? "min-w-0 flex-1 text-[0.875rem] leading-5"
                          : workspaceInternalNavLabelClass(showLabels)
                      }
                    >
                      {isDrawer ? fullLabel : shortLabel}
                    </span>
                  </>
                )}
              </NavLink>
            );

            return (
              <li key={item.id}>
                {collapsed && !isDrawer ? (
                  <SimpleTooltip content={fullLabel}>
                    <div className="w-full">{link}</div>
                  </SimpleTooltip>
                ) : (
                  link
                )}
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
}
